import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { ShieldCheck } from 'lucide-react';

const PROVISIONS = [
  'You are responsible for maintaining the confidentiality of your login credentials and must not share accounts or passwords.',
  'You are responsible for signing out from public or shared devices.',
  'The application automatically signs you out after periods of inactivity, and enforces a maximum session lifetime, for security purposes.',
  'Access to sensitive information (payroll, banking, identity documents, background checks, PII) may require password or MFA re-verification.',
  'Trusted Devices reduce repeated authentication prompts but do not eliminate security protections.',
  'Login activity and security events are logged for auditing, compliance, fraud prevention, and system security.',
  'You must immediately report any suspected unauthorized access to your account.',
  'The platform reserves the right to terminate sessions that appear suspicious or compromised.',
];

/** Blocks the app until the signed-in user accepts the current Terms version. */
export const TermsGate = () => {
  const { user } = useAuth();
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { setNeedsAcceptance(false); return; }
    apiCall('/api/terms/status')
      .then((r) => setNeedsAcceptance(!r.accepted))
      .catch(() => {});
  }, [user]);

  if (!user || !needsAcceptance) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await apiCall('/api/terms/accept', { method: 'POST' });
      setNeedsAcceptance(false);
      toast.success('Thank you — Terms & Conditions accepted.');
    } catch (e) {
      toast.error(e.message || 'Failed to record acceptance');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/80 z-[130] flex items-center justify-center p-4" data-testid="terms-gate-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-cyan-50 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-cyan-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Updated Terms & Conditions</h3>
            <p className="text-xs text-slate-500">Security & privacy update — acceptance required to continue</p>
          </div>
        </div>
        <ul className="space-y-2 text-sm text-slate-600 list-disc pl-5">
          {PROVISIONS.map((p) => <li key={p.slice(0, 30)}>{p}</li>)}
        </ul>
        <p className="text-xs text-slate-400 mt-3">
          Continued use of the application constitutes acceptance of these updated Terms & Conditions.{' '}
          <a href="/terms" target="_blank" rel="noreferrer" className="text-cyan-600 underline">Read the full Terms of Service</a>.
        </p>
        <label className="flex items-start gap-2 mt-4 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                 className="mt-0.5" data-testid="terms-accept-checkbox" />
          I have read and agree to the updated Terms & Conditions.
        </label>
        <button
          onClick={accept}
          disabled={!checked || busy}
          className="w-full h-11 mt-4 rounded-full bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors disabled:opacity-40"
          data-testid="terms-accept-btn"
        >
          {busy ? 'Saving…' : 'Accept & Continue'}
        </button>
      </div>
    </div>
  );
};

export default TermsGate;
